import React, { useMemo } from "react";
import { Box, Typography, Alert } from "@mui/material";
import { useWeddingWizard } from "../../../../context/WeddingWizardContext";
import useDessertsData from "./hooks/useDessertsData";
import DessertTabs from "./components/DessertTabs";
import DessertGrid from "./components/DessertGrid";
import SelectedSummary from "./components/SelectedSummary";

export default function DessertsStep() {
  const { state } = useWeddingWizard();

  // NEW: use v1 flag — desserts.include (old: flowPlan.includeDesserts)
  const requiresDesserts = !!state.desserts?.include;

  const {
    loading,
    items,
    activeSubcat,
    setActiveSubcat,
    subcatWithCounts,
    visibleItems,
    selections, // now coming from state.desserts.selections
    subtotal,
    itemById,
  } = useDessertsData();

  const selectedGroups = useMemo(() => {
    const groups = new Map();
    for (const sel of selections) {
      const it = itemById.get(sel.patisserieId);
      if (!it) continue;
      const qty = Number(sel.quantity) || 0;
      if (qty <= 0) continue;
      const unit = Number(it?.wedding?.price) || 0;
      const sc = it.subcategory || "other";
      if (!groups.has(sc)) groups.set(sc, []);
      groups.get(sc).push({
        name: it.displayName,
        qty,
        total: unit * qty,
      });
    }
    const order = subcatWithCounts.map((s) => s.key);
    return order
      .filter((k) => groups.has(k))
      .map((k) => ({
        key: k,
        label: subcatWithCounts.find((s) => s.key === k)?.label || k,
        rows: groups.get(k).sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [selections, itemById, subcatWithCounts]);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        Add single-serve desserts{" "}
        {requiresDesserts ? "(required)" : "(optional)"}
      </Typography>

      <Alert
        severity="warning"
        sx={{ mb: 2, borderRadius: 2, color: "text.secondary" }}>
        Images are for reference only. Wedding portions are smaller than
        pictured and may be served in cups, on cake boards, or liners.
      </Alert>

      <DessertTabs
        loading={loading}
        subcatWithCounts={subcatWithCounts}
        activeSubcat={activeSubcat}
        setActiveSubcat={setActiveSubcat}
      />

      <DessertGrid
        loading={loading}
        visibleItems={visibleItems}
        selections={selections}
      />

      <SelectedSummary
        selections={selections}
        selectedGroups={selectedGroups}
        subtotal={subtotal}
      />
    </Box>
  );
}
